//#region src/attachments.ts
/**
* What a contact may attach to a widget message.
*
* Shared, because both sides state it: the frame refuses an oversized or
* foreign file before a single byte moves and says why, and the contact API
* refuses the same file again — the frame's check is a courtesy, the API's is
* the rule. One list keeps the two from disagreeing about what "an image" is.
*/
/** Per file. The upload URL is signed for the exact declared size under it. */
const WIDGET_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
/** Per message. A screenshot or three, never an archive of a folder. */
const WIDGET_MESSAGE_MAX_ATTACHMENTS = 5;
/**
* Images only, and only the four formats a browser can both produce as a
* screenshot and draw without a plugin. SVG is deliberately absent: it is a
* document that can carry script, and an agent opens what a contact sends.
*/
const WIDGET_ATTACHMENT_MIME_TYPES = [
	"image/png",
	"image/jpeg",
	"image/gif",
	"image/webp"
];
const isWidgetAttachmentMimeType = (value) => WIDGET_ATTACHMENT_MIME_TYPES.includes(value);
/** The limit as a person reads it — `10 MB`. */
const WIDGET_ATTACHMENT_MAX_LABEL = `${WIDGET_ATTACHMENT_MAX_BYTES / (1024 * 1024)} MB`;

//#endregion
//#region src/errors.ts
/**
* The contract between the widget and the contact API. The widget branches only
* on these.
*/
const WIDGET_ERROR_KEY = {
	ORIGIN_NOT_ALLOWED: "ST_WIDGET_ORIGIN_NOT_ALLOWED",
	ASSERTION_INVALID: "ST_WIDGET_ASSERTION_INVALID",
	ASSERTION_EXPIRED: "ST_WIDGET_ASSERTION_EXPIRED",
	SESSION_TOKEN_MISSING: "ST_WIDGET_SESSION_TOKEN_MISSING",
	SESSION_TOKEN_EXPIRED: "ST_WIDGET_SESSION_TOKEN_EXPIRED",
	SESSION_TOKEN_INVALID: "ST_WIDGET_SESSION_TOKEN_INVALID",
	RATE_LIMITED: "ST_WIDGET_RATE_LIMITED",
	OPERATION_TOO_BROAD: "ST_WIDGET_OPERATION_TOO_BROAD",
	PUBLISHER_NOT_FOUND: "ST_WIDGET_PUBLISHER_NOT_FOUND",
	TICKET_NOT_FOUND: "ST_WIDGET_TICKET_NOT_FOUND",
	ATTACHMENT_TOO_LARGE: "ST_WIDGET_ATTACHMENT_TOO_LARGE",
	ATTACHMENT_TYPE_NOT_ALLOWED: "ST_WIDGET_ATTACHMENT_TYPE_NOT_ALLOWED",
	ATTACHMENT_NOT_UPLOADED: "ST_WIDGET_ATTACHMENT_NOT_UPLOADED",
	MESSAGE_EMPTY: "ST_WIDGET_MESSAGE_EMPTY",
	TOO_MANY_ATTACHMENTS: "ST_WIDGET_TOO_MANY_ATTACHMENTS",
	ATTACHMENT_NOT_FOUND: "ST_WIDGET_ATTACHMENT_NOT_FOUND",
	REQUEST_INVALID: "ST_WIDGET_REQUEST_INVALID",
	INTERNAL_ERROR: "ST_WIDGET_INTERNAL_ERROR"
};
const SESSION_ERROR_KEYS = new Set([
	WIDGET_ERROR_KEY.SESSION_TOKEN_MISSING,
	WIDGET_ERROR_KEY.SESSION_TOKEN_EXPIRED,
	WIDGET_ERROR_KEY.SESSION_TOKEN_INVALID
]);
/**
* A panel left open outlives its session. These are the keys the widget answers
* by re-exchanging its assertion and replaying the call — once.
*
* `ORIGIN_NOT_ALLOWED` is deliberately not among them, and not worth mapping in
* the widget at all: a step-1 rejection carries no CORS header, so the browser
* hands the widget an opaque network failure and the key never arrives.
*/
const isWidgetSessionErrorKey = (key) => key !== void 0 && SESSION_ERROR_KEYS.has(key);

//#endregion
//#region src/geometry.ts
/**
* The launcher and panel sizes the frame asks its host container to take.
*
* They live here rather than in the widget's CSS because the host page's
* container is sized by the loader, which never renders the widget and so can
* never measure it: the frame is the only side that knows how big it wants to
* be, and the loader is the only side that can act on it.
*/
const WIDGET_LAUNCHER_SIZE = 60;
const WIDGET_PANEL_WIDTH = 400;
const WIDGET_PANEL_HEIGHT = 640;
/** Gap between the launcher and the viewport edges, unless `offset` overrides it. */
const WIDGET_DEFAULT_OFFSET = 20;

//#endregion
//#region src/identity.ts
/**
* `aud` of the assertion Hope.Cloud mints for us. One value, always: an
* assertion addressed to support must not be replayable against Jetstream or
* backoffice.
*/
const WIDGET_ASSERTION_AUDIENCE = "support";
/** `aud` of the session SUPPORT issues in exchange. */
const WIDGET_SESSION_AUDIENCE = "stwidget";
/**
* The claims an assertion states, or `undefined` when the string is not a JWT
* this code can read.
*
* NOT verification, and nothing read here may be treated as verified: no
* signature is checked, so every value is whatever the holder of the string
* chose to write. The contact API is the only party that verifies an assertion.
*/
const readAssertionClaims = (userToken) => {
	const segments = userToken.split(".");
	const payload = segments[1];
	if (segments.length !== 3 || !payload) return;
	try {
		const binary = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
		const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
		const claims = JSON.parse(new TextDecoder().decode(bytes));
		return typeof claims === "object" && claims !== null && !Array.isArray(claims) ? claims : void 0;
	} catch {
		return;
	}
};
/**
* Whether an assertion says, in its own claims, that it names no organization.
*
* A widget visitor is always filed under one publisher, and `orgId` is what
* names it. Backoffice mints only for a person with a direct, active role in an
* organization, so a pass without one is a pass backoffice never issued — and
* the widget does not appear for it (see `hasWidgetVisitor`).
*
* This decides what is DRAWN, never what is allowed. The contact API refuses
* the same assertion on its own, by the same test (`toClaims` in
* `widget-assertion.service.ts` — a non-empty string, mirrored here), and it
* would do so whatever this function answered. Reading the claim on the client
* exists because the exchange is deferred to the first open: without it the
* launcher sits on the page until somebody clicks it, and only then does the
* API say there was never anyone to serve.
*
* `false` for a string that cannot be read as a JWT. That is "cannot tell", not
* "names one": the server is asked, exactly as before this function existed.
* Answering `true` there would make the widget's presence depend on the token
* FORMAT — the day the issuer changed it, every embed would go blank at once,
* with nothing failing anywhere.
*/
const assertionLacksOrganization = (userToken) => {
	const claims = readAssertionClaims(userToken);
	if (!claims) return false;
	const orgId = claims["orgId"];
	return typeof orgId !== "string" || orgId.length === 0;
};

//#endregion
//#region src/messages.ts
/**
* Every message carries this marker. The frame's origin is checked too, but a
* host page is a shared bus — analytics, chat vendors and extensions all post
* to it — so a message that merely has a `type` we recognise is not ours.
*/
const WIDGET_MESSAGE_CHANNEL = "hope-support-widget";
const wrapMessage = (message) => ({
	channel: WIDGET_MESSAGE_CHANNEL,
	message
});
const unwrapMessage = (data) => {
	if (typeof data !== "object" || data === null) return;
	const envelope = data;
	if (envelope.channel !== WIDGET_MESSAGE_CHANNEL || typeof envelope.message !== "object") return;
	return envelope.message;
};

//#endregion
//#region src/options.ts
/**
* Whether these settings state a visitor the widget can serve — the one rule
* for who sees it, read by both ends of the frame boundary.
*
* A visitor is a person with a direct, active role in ONE organization, because
* a visitor is always filed under one publisher. Backoffice mints an assertion
* for nobody else: not for a Hope.Cloud superadmin, whose role names no
* organization, and not for someone whose only role spans a group of them.
* Guessing a publisher for such a person would file them into somebody else's
* workspace, so there is no visitor to guess for — and no widget.
*
* So the host states "nobody" by omitting `userToken`, and a token that names
* no organization is the same statement made badly.
*
* Both ends ask, and not for symmetry: the stable `widget.js` and the frame
* deploy separately, so each has to hold the rule against an older copy of the
* other. The loader keeps the frame off the page; the frame draws nothing and
* holds no credentials.
*/
const hasWidgetVisitor = (settings) => typeof settings.userToken === "string" && settings.userToken.length > 0 && !assertionLacksOrganization(settings.userToken);

//#endregion
export { WIDGET_ASSERTION_AUDIENCE, WIDGET_ATTACHMENT_MAX_BYTES, WIDGET_ATTACHMENT_MAX_LABEL, WIDGET_ATTACHMENT_MIME_TYPES, WIDGET_DEFAULT_OFFSET, WIDGET_ERROR_KEY, WIDGET_LAUNCHER_SIZE, WIDGET_MESSAGE_CHANNEL, WIDGET_MESSAGE_MAX_ATTACHMENTS, WIDGET_PANEL_HEIGHT, WIDGET_PANEL_WIDTH, WIDGET_SESSION_AUDIENCE, assertionLacksOrganization, hasWidgetVisitor, isWidgetAttachmentMimeType, isWidgetSessionErrorKey, unwrapMessage, wrapMessage };
//# sourceMappingURL=index.js.map